import { privateClient } from "@/api/privateClient";
import { SUPER_ADMIN_ENDPOINTS } from "../../endpoint";
import type {
  Subject,
  CreateSubjectPayload,
  UpdateSubjectPayload,
  SubjectImportPreviewResult,
  SubjectImportCommitResult,
  SubjectImportPreviewRow,
} from "../types";

export const fetchSubjects = async (): Promise<Subject[]> => {
  const { data } = await privateClient.get(SUPER_ADMIN_ENDPOINTS.SUBJECTS.LIST);
  return data.subjects ?? data;
};

/** Super Admin catalog management view — includes inactive subjects and every field. */
export interface SubjectAdminPage {
  subjects: Subject[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export const fetchSubjectsAdmin = async (params: { page: number; limit: number; search?: string }): Promise<SubjectAdminPage> => {
  const { data } = await privateClient.get(SUPER_ADMIN_ENDPOINTS.SUBJECTS.LIST_ADMIN, { params });
  return data;
};

export const createSubject = async (payload: CreateSubjectPayload): Promise<Subject> => {
  const { data } = await privateClient.post(SUPER_ADMIN_ENDPOINTS.SUBJECTS.CREATE, payload);
  return data.subject ?? data;
};

export const updateSubject = async (id: number, payload: UpdateSubjectPayload): Promise<Subject> => {
  const { data } = await privateClient.put(SUPER_ADMIN_ENDPOINTS.SUBJECTS.UPDATE(id), payload);
  return data.subject ?? data;
};

export const deleteSubject = async (id: number): Promise<void> => {
  await privateClient.delete(SUPER_ADMIN_ENDPOINTS.SUBJECTS.DELETE(id));
};

export const downloadSubjectImportSample = async (): Promise<void> => {
  const { data } = await privateClient.get(SUPER_ADMIN_ENDPOINTS.SUBJECTS.BULK_IMPORT_SAMPLE, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = "subjects-import-sample.xlsx";
  a.click();
  URL.revokeObjectURL(url);
};

export const previewSubjectImport = async (file: File): Promise<SubjectImportPreviewResult> => {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await privateClient.post(SUPER_ADMIN_ENDPOINTS.SUBJECTS.BULK_IMPORT_PREVIEW, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

export const commitSubjectImport = async (
  rows: Pick<SubjectImportPreviewRow, "name" | "code" | "category" | "subjectType">[]
): Promise<SubjectImportCommitResult> => {
  const { data } = await privateClient.post(SUPER_ADMIN_ENDPOINTS.SUBJECTS.BULK_IMPORT_COMMIT, { rows });
  return data;
};
